import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Typography,
    Box,
    Paper,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    CircularProgress,
} from "@mui/material";
import PlayerSearchBar from "./PlayerSearchBar";
import { teamHeaderGradient } from "./utils/teamColors";

interface AttrMeta {
    key: string;
    label: string;
    unit: string;
}

interface ProspectStat {
    value: number | null;
    mlb_avg: number | null;
    mlb_std: number;
}

interface PitchMatch {
    name: string;
    mlbam_id: number | null;
    season: string;
    similarity: number;
    stats: Record<string, number>;
}

interface ProspectPitch {
    pitch_type: string;
    pitch_name: string;
    usage: number;
    pitches: number;
    psStuff: number | null;
    stats: Record<string, ProspectStat>;
    matches: PitchMatch[];
}

interface CompPitch {
    pitch_type: string;
    pitch_name: string;
    usage: number;
    shared: boolean;
    stats: Record<string, number>;
}

interface CompMatch {
    name: string;
    mlbam_id: number | null;
    season: string;
    similarity: number;
    total_pitches: number;
    pitches: CompPitch[];
}

interface CompData {
    name: string;
    id: string;
    level: string;
    season: string;
    throws: string;
    role: string;
    team: string;
    attributes: AttrMeta[];
    pitches: ProspectPitch[];
    matches: CompMatch[];
}

// Shared header cell style (matches MlbComparison)
const TH_SX = {
    color: "white",
    fontWeight: "bold",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
    py: 1,
    px: 1,
};

const NAME_CELL_MIN_W = 130;

// Per-attribute display formatting
function fmtAttr(value: number | null | undefined, key: string): string {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    if (key === "spin") return Math.round(value).toString();
    return value.toFixed(1);
}

function SimBadge({ sim }: { sim: number }) {
    const bg = sim >= 60 ? "#1a7c1a" : sim >= 40 ? "#b8860b" : "#888";
    return (
        <Box
            component="span"
            sx={{
                display: "inline-block",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: "bold",
                color: "white",
                backgroundColor: bg,
                minWidth: 38,
                textAlign: "center",
            }}
        >
            {sim}%
        </Box>
    );
}

const PITCH_COLORS: Record<string, string> = {
    FF: "#d22d49",
    SI: "#fe9d00",
    FC: "#933f2c",
    SL: "#eee716",
    ST: "#ddb33a",
    CU: "#00d1ed",
    KC: "#311d8b",
    SV: "#93afd4",
    CH: "#1dbe3a",
    FS: "#3bacac",
    KN: "#888888",
    DB: "#888888",
};

function PitchChip({ name, color }: { name: string; color: string }) {
    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                fontWeight: 600,
                fontSize: "0.82rem",
            }}
        >
            <Box
                sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    backgroundColor: color,
                    flexShrink: 0,
                }}
            />
            {name}
        </Box>
    );
}

function savantUrl(name: string, id: number): string {
    const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    return `https://baseballsavant.mlb.com/savant-player/${slug}-${id}`;
}

interface MovePoint {
    pt: string;
    name: string;
    x: number;
    y: number;
}

// Movement profile: H-Mov (arm-side +) on X, IVB on Y. Prospect = filled
// circles, comp = hollow squares, both colored by pitch type.
function MovementChart({
    prospect,
    comp,
    compName,
    prospectName,
}: {
    prospect: ProspectPitch[];
    comp: CompPitch[];
    compName: string;
    prospectName: string;
}) {
    const W = 300;
    const H = 300;
    const M = 28;
    const pPts: MovePoint[] = prospect
        .map((p) => ({ pt: p.pitch_type, name: p.pitch_name, x: p.stats.hmov?.value, y: p.stats.ivb?.value }))
        .filter((d) => d.x != null && d.y != null) as MovePoint[];
    const cPts: MovePoint[] = comp
        .map((p) => ({ pt: p.pitch_type, name: p.pitch_name, x: p.stats.hmov, y: p.stats.ivb }))
        .filter((d) => d.x != null && d.y != null) as MovePoint[];
    const all = [...pPts, ...cPts];
    if (all.length === 0) return null;
    const maxAbs = Math.max(12, ...all.map((d) => Math.max(Math.abs(d.x), Math.abs(d.y)))) * 1.1;
    const sx = (v: number) => M + ((v + maxAbs) / (2 * maxAbs)) * (W - 2 * M);
    const sy = (v: number) => H - M - ((v + maxAbs) / (2 * maxAbs)) * (H - 2 * M);
    return (
        <svg width={W} height={H} style={{ flexShrink: 0 }}>
            {/* axes */}
            <line x1={sx(0)} y1={M} x2={sx(0)} y2={H - M} stroke="#ccc" strokeWidth={1} />
            <line x1={M} y1={sy(0)} x2={W - M} y2={sy(0)} stroke="#ccc" strokeWidth={1} />
            <text x={W - M} y={sy(0) - 4} fontSize={9} fill="#9aa3ad" textAnchor="end">
                arm-side →
            </text>
            <text x={sx(0) + 4} y={M + 8} fontSize={9} fill="#9aa3ad">
                ↑ IVB
            </text>
            {/* comp = hollow squares */}
            {cPts.map((d, i) => (
                <rect
                    key={`c${i}`}
                    x={sx(d.x) - 5}
                    y={sy(d.y) - 5}
                    width={10}
                    height={10}
                    fill="none"
                    stroke={PITCH_COLORS[d.pt] ?? "#555"}
                    strokeWidth={2}
                />
            ))}
            {/* prospect = filled circles */}
            {pPts.map((d, i) => (
                <circle key={`p${i}`} cx={sx(d.x)} cy={sy(d.y)} r={5} fill={PITCH_COLORS[d.pt] ?? "#555"} opacity={0.9} />
            ))}
            <text x={M} y={H - 6} fontSize={9} fill="#555">
                ● {prospectName}
            </text>
            <text x={W - M} y={H - 6} fontSize={9} fill="#555" textAnchor="end">
                ▢ {compName}
            </text>
        </svg>
    );
}

export default function PitcherComparison(props: any) {
    const { id } = useParams();
    const [season, setSeason] = useState("");
    const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
    const [data, setData] = useState<CompData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";

    // Available seasons for this player
    useEffect(() => {
        if (!id) return;
        fetch(`${base}/player-seasons/${id}/`)
            .then((r) => r.json())
            .then((d) => {
                if (d.seasons?.length) {
                    setAvailableSeasons(d.seasons);
                    setSeason(d.latest);
                } else {
                    setAvailableSeasons(["2023", "2024", "2025", "2026"]);
                    setSeason("2026");
                }
            })
            .catch(() => {
                setAvailableSeasons(["2023", "2024", "2025", "2026"]);
                setSeason("2026");
            });
    }, [id, base]);

    useEffect(() => {
        if (!id || !season) return;
        setLoading(true);
        setError("");
        fetch(`${base}/pitcher-comparison/${id}/${season}/`)
            .then((res) => res.json())
            .then((d) => {
                if (d.error) {
                    setError(d.error);
                    setData(null);
                } else {
                    setData(d);
                }
                setLoading(false);
            })
            .catch((e) => {
                setError(String(e));
                setLoading(false);
            });
    }, [id, season, base]);

    const attrs = data?.attributes ?? [];

    // Shade a "vs MLB" cell by |z| (how far the pitch is from the MLB same-type avg)
    function cellBg(z: number | null): string {
        if (z === null) return "inherit";
        const a = Math.abs(z);
        if (a < 0.75) return "inherit";
        if (a < 1.5) return "#fff8e1";
        return "#fff3e0";
    }

    return (
        <div style={{ width: "100%", padding: "16px", boxSizing: "border-box", overflowX: "hidden" }}>
            <title>Prospect Savant – Pitch Comparison</title>

            {/* Header */}
            <Paper
                square
                variant="outlined"
                sx={{
                    mb: 2,
                    overflow: "hidden",
                    border: "#293241 solid 2px",
                    boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                }}
            >
                <Box
                    sx={{
                        background: teamHeaderGradient(data?.team),
                        px: { xs: 2, md: 3 },
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    {data && id && (
                        <Avatar
                            src={`https://img.mlbstatic.com/mlb-photos/image/upload/c_fill,g_auto/w_120/v1/people/${id}/headshot/milb/current`}
                            alt={data.name}
                            sx={{ width: 56, height: 56, border: "2px solid #fff", bgcolor: "#fff" }}
                        />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" fontWeight="bold" color="#fff" sx={{ lineHeight: 1.1 }}>
                            {data ? (
                                <a href={`/player/${id}`} style={{ textDecoration: "none", color: "inherit" }}>
                                    {data.name}
                                </a>
                            ) : (
                                "Pitch Comparison"
                            )}
                        </Typography>
                        {data && (
                            <Typography variant="subtitle2" sx={{ color: "#E0FBFC", fontWeight: 400 }}>
                                {data.level} {data.season} — Pitch Comps
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: { md: "auto" }, flexWrap: "wrap" }}>
                        <FormControl sx={{ minWidth: 110, bgcolor: "#fff", borderRadius: 1 }} size="small">
                            <InputLabel>Season</InputLabel>
                            <Select value={season} label="Season" onChange={(e: SelectChangeEvent) => setSeason(e.target.value)}>
                                {availableSeasons.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {s}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <PlayerSearchBar prod={props.prod} urlPrefix="/pitcher-comparison" placeholder="Switch pitcher…" />
                    </Box>
                </Box>
            </Paper>

            {data && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Each pitch vs the MLB average of the same type{data.throws && data.throws !== "nan" ? ` · ${data.throws}HP` : ""}
                    {data.role ? ` · ${data.role}` : ""} · movement shown arm-side positive
                </Typography>
            )}

            {loading && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            )}
            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {/* ── Section 1: Arsenal vs MLB ─────────────────────────────── */}
            {data && !loading && (
                <Paper
                    variant="outlined"
                    square
                    sx={{
                        mb: 4,
                        border: "#293241 solid 2px",
                        boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black",
                    }}
                >
                    <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                        <Table size="small" sx={{ minWidth: 640, tableLayout: "auto" }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#293241" }}>
                                    <TableCell
                                        sx={{
                                            ...TH_SX,
                                            textAlign: "left",
                                            minWidth: NAME_CELL_MIN_W,
                                            position: "sticky",
                                            left: 0,
                                            backgroundColor: "#293241",
                                            zIndex: 2,
                                        }}
                                    >
                                        Pitch
                                    </TableCell>
                                    <TableCell sx={{ ...TH_SX, minWidth: 170 }}>Closest MLB Pitches</TableCell>
                                    <TableCell sx={TH_SX}>Usage</TableCell>
                                    <TableCell sx={TH_SX}>psStuff+</TableCell>
                                    {attrs.map((a) => (
                                        <TableCell key={a.key} sx={TH_SX}>
                                            {a.label}
                                            {a.unit !== "mph" && a.unit !== "rpm" ? ` (${a.unit})` : ""}
                                        </TableCell>
                                    ))}

                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.pitches.map((p, i) => {
                                    const rowBg = i % 2 === 0 ? "white" : "#f9f9f9";
                                    const color = PITCH_COLORS[p.pitch_type] ?? "#555";
                                    return (
                                        <TableRow key={p.pitch_type} sx={{ backgroundColor: rowBg, "&:hover": { backgroundColor: "#e8f0fe" } }}>
                                            <TableCell
                                                sx={{
                                                    position: "sticky",
                                                    left: 0,
                                                    backgroundColor: rowBg,
                                                    zIndex: 1,
                                                    minWidth: NAME_CELL_MIN_W,
                                                    py: 0.75,
                                                    "&:hover": { backgroundColor: "#e8f0fe" },
                                                }}
                                            >
                                                <PitchChip name={p.pitch_name} color={color} />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", py: 0.75, minWidth: 170 }}>
                                                {p.matches.length > 0 ? (
                                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                                        {p.matches.map((m, i) => (
                                                            <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                                                {m.mlbam_id ? (
                                                                    <a
                                                                        href={savantUrl(m.name, m.mlbam_id)}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ color: "#3D5A80", fontWeight: 700, textDecoration: "none", fontSize: "0.82rem" }}
                                                                    >
                                                                        {m.name}
                                                                    </a>
                                                                ) : (
                                                                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>{m.name}</Typography>
                                                                )}
                                                                <SimBadge sim={m.similarity} />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography sx={{ fontSize: "0.8rem", color: "#9aa3ad" }}>—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", fontSize: "0.82rem", color: "text.secondary" }}>
                                                {p.usage.toFixed(0)}%
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", fontSize: "0.82rem", fontWeight: "bold" }}>
                                                {p.psStuff !== null ? p.psStuff.toFixed(0) : "—"}
                                            </TableCell>
                                            {attrs.map((a) => {
                                                const s = p.stats[a.key];
                                                const val = s?.value ?? null;
                                                const avg = s?.mlb_avg ?? null;
                                                const std = s?.mlb_std ?? 0;
                                                const diff = val !== null && avg !== null ? val - avg : null;
                                                const z = diff !== null && std > 0 ? diff / std : null;
                                                return (
                                                    <TableCell
                                                        key={a.key}
                                                        sx={{ textAlign: "center", py: 0.75, backgroundColor: cellBg(z) }}
                                                    >
                                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.25 }}>
                                                            <Typography sx={{ fontSize: "0.84rem", fontWeight: "bold" }}>
                                                                {fmtAttr(val, a.key)}
                                                            </Typography>
                                                            {avg !== null && (
                                                                <Typography sx={{ fontSize: "0.66rem", color: "#9aa3ad" }}>
                                                                    MLB {fmtAttr(avg, a.key)}
                                                                </Typography>
                                                            )}
                                                            {diff !== null && (
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "0.68rem",
                                                                        fontWeight: "bold",
                                                                        color: diff >= 0 ? "#1a7c1a" : "#c62828",
                                                                    }}
                                                                >
                                                                    {diff >= 0 ? "+" : ""}
                                                                    {fmtAttr(diff, a.key)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                );
                                            })}

                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ px: 2, py: 1, borderTop: "1px solid #e0e0e0" }}>
                        <Typography variant="caption" color="text.secondary">
                            Green/red = above/below the MLB average for that pitch type · amber shading = &gt;0.75 SD from MLB
                            average · IVB = induced vertical break, H-Mov positive = arm-side
                        </Typography>
                    </Box>
                </Paper>
            )}

            {/* ── Section 2: Most similar MLB arsenals ──────────────────── */}
            {data && !loading && data.matches.length > 0 && (
                <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Most Similar MLB Arsenals
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        MLB pitchers whose overall arsenal — pitch mix, velocity and movement — most closely matches{" "}
                        {data.name}. Shared pitch types are highlighted. The movement chart overlays both arsenals (● prospect, ▢ comp).
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {data.matches.map((m, i) => (
                            <Paper
                                key={i}
                                variant="outlined"
                                square
                                sx={{
                                    border: "#293241 solid 2px",
                                    boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        px: 2,
                                        py: 1,
                                        backgroundColor: "#3D5A80",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <SimBadge sim={m.similarity} />
                                    {m.mlbam_id ? (
                                        <a
                                            href={savantUrl(m.name, m.mlbam_id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "white", fontWeight: "bold", textDecoration: "none", fontSize: "1rem" }}
                                        >
                                            {m.name}
                                        </a>
                                    ) : (
                                        <Typography sx={{ color: "white", fontWeight: "bold" }}>{m.name}</Typography>
                                    )}
                                    <Typography sx={{ color: "#ABB8C8", fontSize: "0.8rem" }}>
                                        {m.season} · {m.total_pitches.toLocaleString()} pitches
                                    </Typography>
                                </Box>

                                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "stretch" }}>
                                    <TableContainer sx={{ overflowX: "auto", flex: 1 }}>
                                        <Table size="small" sx={{ minWidth: 560 }}>
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: "#5a6b80" }}>
                                                    <TableCell sx={{ ...TH_SX, textAlign: "left", minWidth: 120 }}>Pitch</TableCell>
                                                    <TableCell sx={TH_SX}>Usage</TableCell>
                                                    {attrs.map((a) => (
                                                        <TableCell key={a.key} sx={TH_SX}>
                                                            {a.label}
                                                            {a.unit !== "mph" && a.unit !== "rpm" ? ` (${a.unit})` : ""}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {m.pitches.map((cp, j) => {
                                                    const color = PITCH_COLORS[cp.pitch_type] ?? "#555";
                                                    const rowBg = cp.shared ? "#eef4ff" : j % 2 === 0 ? "white" : "#f9f9f9";
                                                    return (
                                                        <TableRow key={cp.pitch_type} sx={{ backgroundColor: rowBg }}>
                                                            <TableCell sx={{ py: 0.6 }}>
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                                    <PitchChip name={cp.pitch_name} color={color} />
                                                                    {cp.shared && (
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                fontSize: "0.6rem",
                                                                                fontWeight: "bold",
                                                                                color: "#3D5A80",
                                                                                border: "1px solid #3D5A80",
                                                                                borderRadius: 1,
                                                                                px: 0.5,
                                                                            }}
                                                                        >
                                                                            SHARED
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ textAlign: "center", fontSize: "0.82rem", color: "text.secondary" }}>
                                                                {cp.usage.toFixed(0)}%
                                                            </TableCell>
                                                            {attrs.map((a) => (
                                                                <TableCell key={a.key} sx={{ textAlign: "center", fontSize: "0.82rem", py: 0.6 }}>
                                                                    {fmtAttr(cp.stats[a.key], a.key)}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            p: 1,
                                            borderTop: { xs: "1px solid #e0e0e0", md: "none" },
                                            borderLeft: { xs: "none", md: "1px solid #e0e0e0" },
                                            flexShrink: 0,
                                        }}
                                    >
                                        <MovementChart prospect={data.pitches} comp={m.pitches} compName={m.name} prospectName={data.name} />
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                </Box>
            )}
        </div>
    );
}
